import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LockOpen, Sparkles } from "lucide-react";
import { Container, PageLayout, Headline, MutedText, BackButton, SeedTag, HiddenPackBadge } from "../components/ui";
import { Cover } from "../components/Cover";
import { seedLabel } from "../lib/seedLabel";
import { getCatalog } from "../lib/dataSource";
import { useAdminView } from "../lib/adminView";
import type { PackCard } from "../lib/types";

export default function PackDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pack, setPack] = useState<PackCard | null>(null);
  // Without it, a retired pack opened from the admin view's catalogue
  // would never be found here and stay on "Chargement…".
  const adminView = useAdminView();

  useEffect(() => {
    void getCatalog(undefined, { adminView }).then((catalog) => {
      setPack(catalog.packs.find((p) => p.id === id) ?? null);
    });
  }, [id, adminView]);

  if (!pack) {
    return (
      <PageLayout>
        <Container className="pt-8">
          <MutedText>{t("common.loading")}</MutedText>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container className="pt-6 flex flex-col gap-5">
        <BackButton onClick={() => navigate(-1)} />
        <div className="flex gap-4 items-start">
          <div className="w-28 sm:w-36 shrink-0">
            <Cover svg={pack.cover_svg} imageUrl={pack.cover_image_url} imageThumbUrl={pack.cover_image_thumb_url} imageSize="full" title={pack.title} colorway={pack.cover_colorway} colorKey={pack.slug} />
          </div>
          <div className="flex-1">
            <Headline className="text-xl">{pack.title}</Headline>
            {pack.is_active === false && <HiddenPackBadge className="inline-block mt-1" />}
            <MutedText className="mt-1 max-w-prose">{pack.description}</MutedText>
          </div>
        </div>

        {!pack.unlocked ? (
          <Link
            to="/plus"
            className="tap-target inline-flex w-full items-center justify-center gap-2 rounded-card bg-sun text-mint-ink font-heading font-semibold text-sm px-5 py-3 overprint ink-coral"
          >
            <Sparkles size={16} aria-hidden />
            {t("catalog.includedInPlus")}
          </Link>
        ) : (
          <div className="flex items-center gap-2 text-mint font-heading font-semibold text-sm">
            <LockOpen size={16} />
            {t("catalog.unlocked")}
          </div>
        )}

        <div>
          {pack.stories.length === 0 ? (
            <MutedText>{t("catalog.empty")}</MutedText>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {pack.stories.map((s) => (
                <Link key={s.id} to={`/story/${s.id}`} className="flex flex-col gap-1.5">
                  <Cover svg={s.cover_svg} imageUrl={s.cover_image_url} imageThumbUrl={s.cover_image_thumb_url} title={s.title} colorway={s.cover_colorway} colorKey={s.slug} />
                  <p className="font-heading font-semibold text-xs leading-tight line-clamp-2">{s.title}</p>
                  <SeedTag>{seedLabel(s.seed)}</SeedTag>
                </Link>
              ))}
            </div>
          )}
        </div>
      </Container>
    </PageLayout>
  );
}
